import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { AuthService } from '../../oglasnik-v3/shared/services/auth.service';

/** Firma u koju je admin "ušao" preko switchera. */
export interface CompanyContext {
  id: number;
  naziv: string;
}

/**
 * CompanyContextService — ADMIN/SUBADMIN "radi kao" odabrana firma.
 *
 * Kontekst je ISKLJUČIVO u memoriji (edge case #16 iz PLAN-ADMIN-KREIRANJE.md):
 * nikad se ne piše u localStorage, pa ga odjava / reload uvijek ponište.
 * Dodatno ga `effect` čisti čim `isLoggedIn` padne na false.
 *
 * `null` = radim nad vlastitom (kućnom) firmom — backend tada fallback-a na
 * firmu iz tokena jer interceptor ne šalje `companyId`.
 */
@Injectable({ providedIn: 'root' })
export class CompanyContextService {
  private readonly auth = inject(AuthService);

  private readonly context = signal<CompanyContext | null>(null);

  /** Trenutno odabrana firma ili null (= vlastita). */
  readonly selected = this.context.asReadonly();

  /** Je li switcher aktivan (badge "Radite kao: X" se prikazuje). */
  readonly isActive = computed(() => this.context() !== null);

  /** Id za interceptor; null kad je kontekst prazan. */
  readonly companyId = computed<number | null>(() => this.context()?.id ?? null);

  /** Naziv odabrane firme za badge/dialoge. */
  readonly companyNaziv = computed<string | null>(() => this.context()?.naziv ?? null);

  /**
   * Admin ili SUBADMIN radi u ime DRUGE firme (ne svoje kućne). Backend tada neke radnje izvođača odbija, npr.
   * uključivanje privole „WhatsApp: novi upiti” (403; privolu daje izvođač sam, admin je smije samo isključiti).
   */
  readonly radiUImeDrugeFirme = computed(() => this.isActive() && this.companyId() !== this.auth.companyId());

  /** Switcher smiju vidjeti samo admin i subadmin (autorizaciju radi backend). */
  readonly canSwitch = computed(() => {
    const role = this.auth.role();
    return role === 'admin' || role === 'subadmin';
  });

  constructor() {
    // Edge #16: kontekst NE preživljava odjavu.
    effect(() => {
      if (!this.auth.isLoggedIn()) this.context.set(null);
    });
  }

  enter(company: CompanyContext): void {
    this.context.set(company);
  }

  clear(): void {
    this.context.set(null);
  }
}
